/**
 * Typed API client for portal-side endpoints.
 * Separate from staff lib/api.ts to keep portal isolated.
 * All requests use credentials: 'include' to send the portal cookie.
 */

export type AvailabilityMode = 'RESERVED' | 'CUSTOM' | 'ON_CALL' | 'UNAVAILABLE';
export type AvailabilityStatus =
  | 'PENDING_APPROVAL'
  | 'PENDING_OVERFLOW'
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'COMPLETED'
  /** Manager recorded that she rang ahead and pulled out of a shift. */
  | 'CALLED_OFF';
export type CancelReason =
  | 'SICK'
  | 'PERSONAL_EMERGENCY'
  | 'TRANSPORT_FAILURE'
  | 'OTHER'
  | 'STAFF_CANCELLED';

export interface PortalReservationSlot {
  id: string;
  startTime: string;       // "HH:MM"
  endTime: string;         // "HH:MM"
  isCancelled: boolean;
}

export interface PortalDeclaration {
  id: string;
  date: string;            // "YYYY-MM-DD"
  mode: AvailabilityMode;
  status: AvailabilityStatus;
  reservation: PortalReservationSlot | null;
  customStart: string | null;
  customEnd: string | null;
  unavailableReason: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  cancelledAt: string | null;
  cancelReason: CancelReason | null;
  cancelNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A group of still-identical spots, as offered to a provider.
 *
 * Spots are individual rows, but four unclaimed openings at the same time are
 * one choice from her side, so the server collapses them. Once one diverges it
 * arrives as its own group — which is precisely when the difference matters to
 * her. `id` is a real spot to claim (the first free one), not a group id;
 * groups have no identity of their own.
 */
export interface PortalReservation {
  id: string | null;
  date: string;
  section: string;
  startTime: string;
  endTime: string;
  notes: string | null;
  spotIds: string[];
  availableCount: number;
  totalSpots: number;
  isFull: boolean;
}

export interface PortalAvailabilityWeek {
  weekStart: string;
  weekEnd: string;
  declarations: PortalDeclaration[];
}

export interface PortalReservationsWeek {
  weekStart: string;
  weekEnd: string;
  reservations: PortalReservation[];
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as T;
}

export const portalDataApi = {
  getAvailability: (week: string) =>
    jsonFetch<PortalAvailabilityWeek>(`/api/portal/availability?week=${encodeURIComponent(week)}`),

  getReservations: (week: string) =>
    jsonFetch<PortalReservationsWeek>(`/api/portal/reservations?week=${encodeURIComponent(week)}`),

  declare: (body: {
    date: string;
    mode: AvailabilityMode;
    reservationId?: string;
    customStart?: string;
    customEnd?: string;
    unavailableReason?: string;
  }) =>
    jsonFetch<PortalDeclaration>('/api/portal/availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  cancel: (id: string, cancelReason: CancelReason, cancelNotes?: string) =>
    jsonFetch<PortalDeclaration>(`/api/portal/availability/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cancelReason, cancelNotes }),
    }),
};


// --- Time range helpers -----------------------------------------------------

/**
 * Returns true if endTime is on the next calendar day (crosses midnight).
 * Both inputs are "HH:MM" strings. Same time -> not next day.
 */
export function isOvernight(startTime: string, endTime: string): boolean {
  return endTime <= startTime && endTime !== startTime;
}

/**
 * Format a time range for display, appending "next day" when it crosses
 * midnight. e.g. "20:00 \u2013 04:00 next day".
 */
export function formatTimeRange(startTime: string, endTime: string): string {
  const base = `${startTime} \u2013 ${endTime}`;
  return isOvernight(startTime, endTime) ? `${base} next day` : base;
}

/**
 * Duration in minutes between two HH:MM strings, handling cross-midnight.
 * 18:00 -> 03:00 returns 540 (9h). 20:00 -> 04:00 returns 480 (8h).
 */
export function durationMinutes(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const start = sh * 60 + sm;
  const end = eh * 60 + em;
  if (end > start) return end - start;
  if (end < start) return (24 * 60 - start) + end;
  return 0; // same time = 0 (caller should reject)
}
